import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { checkPasswordComplexity } from "check-password-complexity";

import { useTranslation } from "react-i18next";

import {
	faCheckCircle,
	faDownload,
	faEye,
	faEyeSlash,
	faFileLines,
	faShield,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import gridBlocks from "@/assets/images/landing-page/grid blocks.png";

import { TokenPurpose } from "@/enum";

import { classNames } from "@/utils/classNames";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { Icon } from "@/components";

import { Button } from "@/components/Button/Button";

import { SetPasswordSchema, SetPasswordType } from "./schema/set-password";

import {
	useDownloadResultsMutation,
	useFetchMeByInvitationTokenQuery,
	useSetPasswordMutation,
} from "../../redux/auth.api";
import FormContainer from "../../components/FormContainer";

export const SetPassword: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`set-password.${key}`, options);
	const [passwordFieldType, setPasswordFieldType] = useState("password");
	const [passwordComplexity, setPasswordComplexity] = useState<number>(0);

	const [searchParams] = useSearchParams();

	const [downloadPdf, { isLoading: isDownloadingPdf }] = useDownloadResultsMutation();

	const [setPassword, { isLoading, error }] = useSetPasswordMutation();
	const { data: fetchMeByInvitationTokenData } = useFetchMeByInvitationTokenQuery(
		searchParams.get("token") || ""
	);

	const {
		register,
		handleSubmit,
		formState: { errors },
		watch,
	} = useForm<SetPasswordType>({
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
		resolver: zodResolver(SetPasswordSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (formValues.password) {
			const complexity = checkPasswordComplexity(formValues.password);

			setPasswordComplexity(complexity.score);
		} else {
			setPasswordComplexity(0);
		}
	}, [formValues]);

	const onSubmit = async (values: SetPasswordType) => {
		try {
			await setPassword({
				token: searchParams.get("token") || "",
				purpose: TokenPurpose.REGISTRATION,
				password: values.password,
			}).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const tileColor = useMemo(() => {
		if (passwordComplexity >= 5) {
			return "bg-status-success";
		} else if (passwordComplexity >= 3) {
			return "bg-status-warning";
		} else if (passwordComplexity >= 1) {
			return "bg-status-error";
		}

		return "bg-gray-200";
	}, [passwordComplexity]);

	const passwordIncludesNumbers = useMemo(() => {
		return formValues.password?.match(/\d/);
	}, [formValues.password]);

	const passwordIncludesSpecialCharacters = useMemo(() => {
		return formValues.password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/);
	}, [formValues.password]);

	const passwordIncludesUppercase = useMemo(() => {
		return formValues.password.match(/[A-Z]/);
	}, [formValues.password]);

	const passwordIncludesLowercase = useMemo(() => {
		return formValues.password.match(/[a-z]/);
	}, [formValues.password]);

	const handleDownloadPdf = async () => {
		const response = await downloadPdf({ token: searchParams.get("token") || "" }).unwrap();

		const url = window.URL.createObjectURL(response);

		const a = document.createElement("a");

		a.href = url;
		a.download = `nis360-compliance-roadmap.pdf`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);

		window.URL.revokeObjectURL(url);
	};

	return (
		<>
			<section className="relative w-full bg-surface py-6 sm:py-12 px-4 flex justify-center overflow-hidden">
				<div
					className="absolute inset-0 bg-no-repeat bg-cover bg-center z-0 opacity-[0.04]"
					style={{ backgroundImage: `url(${gridBlocks})` }}
				/>
				<div className="relative z-10 flex flex-col md:flex-row justify-center items-center max-w-[1500px] w-full">
					<div className="flex flex-col max-w-[694px] w-full">
						<div className="flex flex-col gap-6 items-center">
							<h1 className="font-urbanist font-bold text-ink text-xl md:text-lg leading-[120%]">
								{fetchMeByInvitationTokenData?.firstName &&
									ts("title", { name: fetchMeByInvitationTokenData?.firstName })}
							</h1>
							<p className="font-urbanist font-medium text-subtle text-center text-md md:text-base leading-[140%]">
								{ts("description")}
							</p>
						</div>
					</div>
				</div>
			</section>
			<div
				className={`w-full min-h-[calc(100%-96px-6em)] py-6 flex flex-col items-center justify-center gap-7`}
			>
				<div className="p-1 flex shadow-lg hover:shadow-xl transition-shadow duration-300 relative bg-surface font-urbanist rounded-xl w-[600px] max-w-[100%] border border-border flex flex-col gap-4">
					<div className="bg-white p-6 shadow-sm rounded-lg">
						<div className="flex items-center gap-2 mb-2">
							<Icon className="w-5 h-5 text-text-primary" icon={faFileLines} />
							<p className="text-primary text-ssm font-semibold">{ts("pdfTitle")}</p>
						</div>
						<div className="flex items-center gap-2 mb-10">
							<p className="text-primary text-sm font-medium">{ts("pdfSubtitle")}</p>
						</div>
						<p className="text-primary text-ssm font-semibold p-4">
							{ts("pdfContent")}
							<ul className="mt-2 gap-2 flex flex-col">
								<li className="text-primary text-sm font-medium leading-[20px]">
									<Icon className="w-4 h-4 text-primary-gray-lighter" icon={faCheckCircle} />{" "}
									{ts("pdfItemTasks")}
								</li>
								<li className="text-primary text-sm font-medium leading-[20px]">
									<Icon
										className="w-4 h-4 text-primary-gray-lighter"
										icon={faTriangleExclamation}
									/>{" "}
									{ts("pdfItemDescriptions")}
								</li>
								<li className="text-primary text-sm font-medium leading-[20px]">
									<Icon className="w-4 h-4 text-primary-gray-lighter" icon={faShield} />{" "}
									{ts("pdfItemChecklists")}
								</li>
							</ul>
						</p>
						<div className="flex flex-row gap-2">
							<Button
								image={<Icon className="w-4 h-4 text-white mr-2" icon={faDownload} />}
								isLoading={isDownloadingPdf}
								title={ts("downloadPdf")}
								onClick={handleDownloadPdf}
							/>
						</div>
					</div>
				</div>

				<FormContainer
					description={ts("formDescription")}
					formContainerClassName="h-content"
					formError={error}
					handleSubmit={handleSubmit(onSubmit)}
					isLoading={isLoading}
					showLogo={false}
					submitButtonText={ts("confirm")}
					title={ts("secondTitle")}
				>
					<FormTextField
						autoComplete="password"
						endAdornment={
							<button
								aria-label={
									passwordFieldType === "password" ? ts("showPassword") : ts("hidePassword")
								}
								type="button"
								onClick={() =>
									setPasswordFieldType(passwordFieldType === "password" ? "text" : "password")
								}
							>
								<Icon
									className="w-4 h-4 text-primary-gray-lighter"
									icon={passwordFieldType === "password" ? faEye : faEyeSlash}
								/>
							</button>
						}
						errorMessage={errors?.password?.message}
						formRegister={register("password")}
						label={t("basics.password")}
						placeHolder={t("basics.enterPassword")}
						type={passwordFieldType}
					/>
					<FormTextField
						autoComplete="confirmPassword"
						endAdornment={
							<button
								aria-label={
									passwordFieldType === "password" ? ts("showPassword") : ts("hidePassword")
								}
								type="button"
								onClick={() =>
									setPasswordFieldType(passwordFieldType === "password" ? "text" : "password")
								}
							>
								<Icon
									className="w-4 h-4 text-primary-gray-lighter"
									icon={passwordFieldType === "password" ? faEye : faEyeSlash}
								/>
							</button>
						}
						errorMessage={errors?.confirmPassword?.message}
						formRegister={register("confirmPassword")}
						label={t("basics.confirmPassword")}
						placeHolder={t("basics.enterConfirmPassword")}
						type={passwordFieldType}
					/>
					<div className="flex flex-row gap-2">
						<div
							className={classNames(
								"w-1/3 h-[3px] bg-gray-200 rounded-md",
								passwordComplexity >= 1 ? tileColor : "bg-gray-200"
							)}
						></div>
						<div
							className={classNames(
								"w-1/3 h-[3px] bg-gray-200 rounded-md",
								passwordComplexity >= 3 ? tileColor : "bg-gray-200"
							)}
						></div>
						<div
							className={classNames(
								"w-1/3 h-[3px] bg-gray-200 rounded-md",
								passwordComplexity >= 5 ? tileColor : "bg-gray-200"
							)}
						></div>
					</div>
					{formValues.password?.length > 1 && (
						<div className="text-left text-xs font-normal w-full mb-4">
							{formValues.password?.length < 8 && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{ts("passwordMin8")}
								</div>
							)}
							{!passwordIncludesNumbers && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{ts("passwordNumber")}
								</div>
							)}
							{!passwordIncludesSpecialCharacters && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{ts("passwordSpecial")}
								</div>
							)}
							{!passwordIncludesUppercase && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{ts("passwordUppercase")}
								</div>
							)}
							{!passwordIncludesLowercase && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{ts("passwordLowercase")}
								</div>
							)}
						</div>
					)}
				</FormContainer>
			</div>
		</>
	);
};
